import React, { useState, useEffect, useCallback } from 'react';
import { 
  Link2, Plus, Edit, Trash2, Save, X, RefreshCw, ExternalLink,
  BookOpen, GraduationCap, Smartphone, FileText, Eye, EyeOff
} from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { toast } from 'sonner';

const API_BASE = 'https://campusapi.ohcampus.com/apps/Chatbot';

const CATEGORIES = [
  { value: 'predictor', label: 'Predictor Tools', icon: GraduationCap },
  { value: 'exam', label: 'Exams', icon: FileText },
  { value: 'course', label: 'Courses', icon: BookOpen },
  { value: 'app', label: 'App Links', icon: Smartphone },
  { value: 'general', label: 'General', icon: Link2 }
];

const ChatbotSourceLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    category: 'general',
    description: '',
    is_active: 1
  });

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/getSourceLinks_api`);
      const data = await response.json();
      if (data.response_code === '200') {
        setLinks(data.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch source links');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.url) {
      toast.error('Title and URL are required');
      return;
    }

    try {
      const endpoint = editingLink ? 'updateSourceLink' : 'addSourceLink';
      const body = editingLink ? { ...formData, id: editingLink.id } : formData;
      
      const response = await fetch(`${API_BASE}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      
      if (data.response_code === '200') {
        toast.success(editingLink ? 'Link updated' : 'Link added');
        setShowForm(false);
        setEditingLink(null);
        setFormData({ title: '', url: '', category: 'general', description: '', is_active: 1 });
        fetchLinks();
      } else {
        toast.error(data.response_message);
      }
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this source link?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/deleteSourceLink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      const data = await response.json();
      
      if (data.response_code === '200') {
        toast.success('Link deleted');
        fetchLinks();
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const toggleActive = async (link) => {
    try {
      await fetch(`${API_BASE}/updateSourceLink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: link.id, is_active: Number(link.is_active) === 1 ? 0 : 1 })
      });
      fetchLinks();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const startEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url,
      category: link.category,
      description: link.description || '',
      is_active: link.is_active
    });
    setShowForm(true);
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    const Icon = cat ? cat.icon : Link2;
    return <Icon className="w-4 h-4" />;
  };

  const getCategoryLabel = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const groupedLinks = CATEGORIES.map(cat => ({
    ...cat,
    links: links.filter(l => l.category === cat.value)
  })).filter(cat => cat.links.length > 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Chatbot Source Links</h1>
            <p className="text-gray-500">Manage knowledge base links for AI chatbot (hidden from users)</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchLinks} className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button 
              onClick={() => { setShowForm(true); setEditingLink(null); setFormData({ title: '', url: '', category: 'general', description: '', is_active: 1 }); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Add Source Link
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-800 mb-2">How Source Links Work</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Source links provide knowledge to the AI chatbot about OhCampus resources</li>
            <li>• The AI uses this information to answer queries accurately</li>
            <li>• URLs are NOT shown to users in chat responses (they're internal references)</li>
            <li>• For Predictor queries from website, the AI recommends downloading the mobile app</li>
          </ul>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4">{editingLink ? 'Edit Source Link' : 'Add New Source Link'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g., KCET College Predictor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="https://ohcampus.com/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (for AI context)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  rows="2"
                  placeholder="Brief description of what this resource contains..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={Number(formData.is_active) === 1}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})}
                  className="rounded"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">Active (include in AI knowledge base)</label>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                  <Save className="w-4 h-4" /> {editingLink ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditingLink(null); }} className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Links List */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : groupedLinks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No source links found. Add your first link!</div>
        ) : (
          <div className="space-y-6">
            {groupedLinks.map(group => (
              <div key={group.value} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2">
                  <group.icon className="w-5 h-5 text-gray-600" />
                  <h3 className="font-medium text-gray-800">{group.label}</h3>
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{group.links.length}</span>
                </div>
                <div className="divide-y">
                  {group.links.map(link => (
                    <div key={link.id} className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${Number(link.is_active) === 0 ? 'opacity-50' : ''}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">{link.title}</span>
                          {Number(link.is_active) === 0 && <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded">Inactive</span>}
                        </div>
                        <p className="text-sm text-gray-500 truncate max-w-md">{link.url}</p>
                        {link.description && <p className="text-xs text-gray-400 mt-1">{link.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Open Link">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button onClick={() => toggleActive(link)} className={`p-2 rounded-lg ${Number(link.is_active) === 1 ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={Number(link.is_active) === 1 ? 'Deactivate' : 'Activate'}>
                          {Number(link.is_active) === 1 ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => startEdit(link)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(link.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ChatbotSourceLinks;
